import React from 'react';

// ============================================
// Types
// ============================================

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'white' | 'slate';
  className?: string;
}

interface FullPageLoaderProps {
  message?: string;
}

// ============================================
// Styles
// ============================================

const sizeStyles = {
  sm: 'w-4 h-4 border-2',
  md: 'w-8 h-8 border-2',
  lg: 'w-12 h-12 border-4',
};

const colorStyles = {
  primary: 'border-indigo-200 border-t-indigo-600',
  white: 'border-white/30 border-t-white',
  slate: 'border-slate-200 border-t-slate-600',
};

// ============================================
// Spinner Component
// ============================================

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className = '',
}) => {
  return (
    <div 
      className={`
        ${sizeStyles[size]} 
        ${colorStyles[color]} 
        rounded-full animate-spin
        ${className}
      `}
      role="status"
      aria-label="Loading"
    />
  );
};

// ============================================
// Full Page Loader Component
// ============================================

export const FullPageLoader: React.FC<FullPageLoaderProps> = ({
  message = 'Loading...',
}) => {
  return (
    <div className="flex h-screen items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <LoadingSpinner size="lg" />
        <p className="text-slate-500 font-bold uppercase tracking-widest text-[10px] animate-pulse">
          {message}
        </p>
      </div>
    </div>
  );
};

// ============================================
// Inline Loader Component
// ============================================

interface InlineLoaderProps {
  message?: string;
  className?: string;
}

export const InlineLoader: React.FC<InlineLoaderProps> = ({
  message,
  className = '',
}) => {
  return (
    <div className={`flex items-center justify-center gap-3 p-4 ${className}`}>
      <LoadingSpinner size="sm" />
      {message && (
        <span className="text-slate-500 text-sm font-medium">{message}</span>
      )}
    </div>
  );
};

// ============================================
// Skeleton Loader Component
// ============================================

interface SkeletonProps {
  width?: string;
  height?: string;
  rounded?: 'sm' | 'md' | 'lg' | 'full';
  className?: string;
}

const roundedStyles = {
  sm: 'rounded',
  md: 'rounded-lg',
  lg: 'rounded-2xl',
  full: 'rounded-full',
};

export const Skeleton: React.FC<SkeletonProps> = ({
  width = 'w-full',
  height = 'h-4',
  rounded = 'md',
  className = '',
}) => {
  return (
    <div 
      className={`
        ${width} ${height} 
        ${roundedStyles[rounded]}
        bg-slate-200 animate-pulse
        ${className}
      `}
      aria-hidden="true"
    />
  );
};

export default LoadingSpinner;
